import React, { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "react-query";
import { getErrorMessage } from "services/api";
import {
  getEmployeesGroups,
  deleteEmployeesGroup,
  getEmployees,
  deleteEmployee,
} from "features/Employees/api";
import { EmployeeGroupModal } from "features/Employees/EmployeeGroupModal";
import { EmployeeModal } from "features/Employees/EmployeeModal";
import { AssignEmployeeModal } from "features/Employees/AssignEmployeeModal";

import { PageLayout, ActionsRow } from "components/PageLayout";
import { BoldText } from "components/Text";
import { Button } from "components/Button";
import { Table } from "components/Table";
import { SearchInput } from "components/SearchInput";
import { SimpleList, SimpleListRow } from "components/SimpleList";
import { useModal, ConfirmationModal, InfoModal } from "components/Modal";

import styles from "./styles.module.scss";

const GroupsPanel = ({ activeGroup, onSelectGroup }) => {
  const queryClient = useQueryClient();
  const { openModal, closeModal } = useModal();

  const groupsQuery = useQuery(["employees-groups"], getEmployeesGroups);
  const groups = groupsQuery.data?.items ?? [];

  const openGroupModal = (isEdit) =>
    openModal(<EmployeeGroupModal isEdit={isEdit} group={activeGroup} />);

  const onDeleteClick = () => {
    if (!activeGroup) return;
    openModal(
      <ConfirmationModal
        title={`Вы действительно хотите удалить должность "${activeGroup.item.name}"?`}
        onConfirm={async () => {
          try {
            await deleteEmployeesGroup(activeGroup.item.usersgroupid);
            queryClient.invalidateQueries(["employees-groups"]);
            onSelectGroup(null);
            closeModal();
          } catch (e) {
            openModal(
              <InfoModal title={getErrorMessage(e)} onClose={closeModal} />
            );
          }
        }}
        onClose={closeModal}
      />
    );
  };

  return (
    <PageLayout
      className={styles.leftPanel}
      header={<BoldText>Должности</BoldText>}
      footer={
        <ActionsRow>
          <Button
            icon="plus"
            onClick={() => openGroupModal(false)}
            title="Добавить группу"
          />
          <Button
            icon="delete"
            onClick={onDeleteClick}
            disabled={!activeGroup}
            title="Удалить группу"
          />
          <Button
            icon="info"
            onClick={() => openGroupModal(true)}
            disabled={!activeGroup}
            title="Свойства группы"
          />
        </ActionsRow>
      }
    >
      <SimpleList
        items={groups}
        rowKey={(g) => g.item.usersgroupid}
        getLabel={(g) => g.item.name}
        activeId={activeGroup?.item?.usersgroupid}
        loading={groupsQuery.isLoading}
        error={groupsQuery.isError ? getErrorMessage(groupsQuery.error) : ""}
        onRetry={groupsQuery.refetch}
        topRow={
          <SimpleListRow
            label="Все"
            isActive={!activeGroup}
            onClick={() => onSelectGroup(null)}
          />
        }
        onRowClick={(g) => onSelectGroup(g)}
        onRowOpen={() => openGroupModal(true)}
      />
    </PageLayout>
  );
};

const EmployeesPanel = ({ activeGroup }) => {
  const queryClient = useQueryClient();
  const { openModal, closeModal } = useModal();

  const [search, setSearch] = useState("");
  const [selectedEmployee, setSelectedEmployee] = useState(null);

  const groupId = activeGroup?.item?.usersgroupid ?? null;

  const employeesQuery = useQuery(["employees", groupId], () =>
    getEmployees({ groupId })
  );
  const employees = useMemo(
    () => employeesQuery.data?.items ?? [],
    [employeesQuery.data]
  );

  const filtered = useMemo(() => {
    if (!search) return employees;
    return employees.filter((e) =>
      e.name?.toLowerCase().includes(search.toLowerCase())
    );
  }, [employees, search]);

  const colDefs = useMemo(
    () => [
      { field: "name", headerName: "ФИО", flex: 1 },
      { field: "code", headerName: "Код сотрудника", width: 200 },
    ],
    []
  );

  const openEmployeeModal = (isEdit, employee) =>
    openModal(
      <EmployeeModal isEdit={isEdit} employee={employee} group={activeGroup} />
    );

  const onAddClick = () => {
    openEmployeeModal(false, null);
  };

  const onDeleteClick = () => {
    if (!selectedEmployee) return;
    openModal(
      <ConfirmationModal
        title={`Вы действительно хотите удалить сотрудника "${selectedEmployee.name}"?`}
        onConfirm={async () => {
          try {
            await deleteEmployee(selectedEmployee.usersid);
            setSelectedEmployee(null);
            queryClient.invalidateQueries(["employees"]);
            closeModal();
          } catch (e) {
            openModal(
              <InfoModal title={getErrorMessage(e)} onClose={closeModal} />
            );
          }
        }}
        onClose={closeModal}
      />
    );
  };

  return (
    <PageLayout
      className={styles.rightPanel}
      header={<BoldText>Сотрудники</BoldText>}
      footer={
        <ActionsRow>
          <Button icon="plus" onClick={onAddClick}>
            Добавить сотрудника
          </Button>
          <Button
            icon="move"
            onClick={() =>
              openModal(<AssignEmployeeModal employee={selectedEmployee} />)
            }
            disabled={!selectedEmployee}
          >
            Назначить
          </Button>
          <Button
            icon="delete"
            onClick={onDeleteClick}
            disabled={!selectedEmployee}
            title="Удалить сотрудника"
          />
        </ActionsRow>
      }
      bodyFill
    >
      <div className={styles.searchRow}>
        <SearchInput value={search} onChange={setSearch} placeholder="Поиск" />
      </div>
      <Table
        fill
        rowData={filtered}
        colDefs={colDefs}
        rowKey="usersid"
        selectedId={selectedEmployee?.usersid}
        onSelect={(row) =>
          setSelectedEmployee(
            employees.find((e) => e.usersid === row.usersid) || null
          )
        }
        onOpen={(row) => {
          const employee = employees.find((e) => e.usersid === row.usersid);
          if (employee) {
            setSelectedEmployee(employee);
            openEmployeeModal(true, employee);
          }
        }}
        loading={employeesQuery.isLoading}
        error={
          employeesQuery.isError ? getErrorMessage(employeesQuery.error) : ""
        }
        onRetry={employeesQuery.refetch}
      />
    </PageLayout>
  );
};

export const EmployeesPage = () => {
  const [activeGroup, setActiveGroup] = useState(null);

  return (
    <div className={styles.page}>
      <GroupsPanel activeGroup={activeGroup} onSelectGroup={setActiveGroup} />
      <EmployeesPanel activeGroup={activeGroup} />
    </div>
  );
};
